import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import {
  fetchAdminUserDetail, updateAdminUser, blacklistUser, unblacklistUser,
  deleteAdminUser, updateSubscription, adjustWallet, resetUserQueries,
} from '../../api/admin';
import { AdminConfirmModal } from '../../components/admin';
import { FiArrowLeft, FiEdit2, FiShield, FiTrash2, FiDollarSign, FiRefreshCw } from 'react-icons/fi';

const AdminUserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const colors = useAppSelector((state) => state.theme.theme);
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'profile' | 'bookings' | 'transactions' | 'copilot'>('profile');
  const [modal, setModal] = useState<{ type: string; open: boolean }>({ type: '', open: false });

  // Edit state
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState<Record<string, any>>({});

  // Wallet adjust state
  const [walletAmount, setWalletAmount] = useState('');
  const [walletType, setWalletType] = useState('DEPOSIT');
  const [walletDesc, setWalletDesc] = useState('');

  // Subscription state
  const [subTier, setSubTier] = useState('');
  const [subExpires, setSubExpires] = useState('');

  const userId = parseInt(id || '0');

  const load = () => {
    setLoading(true);
    fetchAdminUserDetail(userId)
      .then((res) => {
        setUser(res.data);
        if (res.data.profile) {
          setSubTier(res.data.profile.subscription_tier);
          setSubExpires(res.data.profile.subscription_expires || '');
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [userId]); // eslint-disable-line

  const handleSaveEdit = async () => {
    try {
      await updateAdminUser(userId, editForm);
      setEditMode(false);
      load();
    } catch {}
  };

  const handleBlacklist = async () => {
    try {
      if (user.is_blacklisted) {
        await unblacklistUser(userId);
      } else {
        await blacklistUser(userId, 'Admin action');
      }
      setModal({ type: '', open: false });
      load();
    } catch {}
  };

  const handleDelete = async () => {
    try {
      await deleteAdminUser(userId);
      setModal({ type: '', open: false });
      navigate('/admin/users');
    } catch {}
  };

  const handleSubUpdate = async () => {
    try {
      await updateSubscription(userId, { subscription_tier: subTier, subscription_expires: subExpires || null });
      load();
    } catch {}
  };

  const handleWalletAdjust = async () => {
    if (!walletAmount) return;
    try {
      await adjustWallet(userId, { amount: parseFloat(walletAmount), transaction_type: walletType, description: walletDesc });
      setWalletAmount('');
      setWalletDesc('');
      load();
    } catch {}
  };

  const handleResetQueries = async () => {
    try {
      await resetUserQueries(userId);
      load();
    } catch {}
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: colors.primary, borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (!user) {
    return <div className="p-6 text-center" style={{ color: colors.grayScale500 }}>User not found</div>;
  }

  const tabs = ['profile', 'bookings', 'transactions', 'copilot'] as const;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/admin/users')} className="p-2 rounded-lg" style={{ backgroundColor: colors.inputBackground }}>
          {React.createElement(FiArrowLeft as any, { size: 18, color: colors.textColor })}
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold" style={{ color: colors.textColor }}>
            {user.profile?.name || user.email || `User #${user.id}`}
          </h1>
          <p className="text-xs" style={{ color: colors.grayScale500 }}>ID: {user.id} | Joined: {user.date_joined?.split('T')[0]}</p>
        </div>
        {/* Action buttons */}
        <button onClick={() => setEditMode(!editMode)} className="p-2 rounded-lg" style={{ backgroundColor: `${colors.primary}15` }}>
          {React.createElement(FiEdit2 as any, { size: 16, color: colors.primary })}
        </button>
        <button onClick={() => setModal({ type: 'blacklist', open: true })} className="p-2 rounded-lg" style={{ backgroundColor: user.is_blacklisted ? '#10B98120' : '#EF444420' }}>
          {React.createElement(FiShield as any, { size: 16, color: user.is_blacklisted ? '#10B981' : '#EF4444' })}
        </button>
        <button onClick={() => setModal({ type: 'delete', open: true })} className="p-2 rounded-lg" style={{ backgroundColor: '#EF444420' }}>
          {React.createElement(FiTrash2 as any, { size: 16, color: '#EF4444' })}
        </button>
      </div>

      {/* Status badges */}
      <div className="flex flex-wrap gap-2 mb-6">
        {user.is_blacklisted && <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: '#EF444420', color: '#EF4444' }}>Blacklisted</span>}
        {user.is_superuser && <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: '#8B5CF620', color: '#8B5CF6' }}>Admin</span>}
        {user.is_verified && <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: '#10B98120', color: '#10B981' }}>Verified</span>}
        {user.profile?.is_expert && <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: '#F59E0B20', color: '#F59E0B' }}>Expert</span>}
        <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: `${colors.primary}15`, color: colors.primary }}>
          {user.profile?.subscription_tier || 'free'}
        </span>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b" style={{ borderColor: colors.borderColor }}>
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className="px-4 py-2.5 text-sm font-medium capitalize transition-colors"
            style={{
              color: activeTab === tab ? colors.primary : colors.grayScale500,
              borderBottom: activeTab === tab ? `2px solid ${colors.primary}` : '2px solid transparent',
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'profile' && (
        <div className="space-y-6">
          {/* Basic info */}
          <div className="rounded-xl p-5" style={{ backgroundColor: colors.cardBackground || colors.backgroundColor, boxShadow: colors.shadowSoft }}>
            <h3 className="font-semibold mb-4" style={{ color: colors.textColor }}>User Info</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              {[
                ['Email', user.email],
                ['Phone', user.phone_number],
                ['Auth Type', user.auth_type],
                ['Name', user.profile?.name],
                ['Wallet Balance', `Rs ${user.profile?.wallet_balance || '0.00'}`],
                ['Sessions', user.profile?.total_completed_sessions],
                ['Rating', user.profile?.user_avg_rating?.toFixed(1)],
                ['Copilot Queries', user.profile?.copilot_queries_used],
              ].map(([label, value]) => (
                <div key={String(label)}>
                  <p style={{ color: colors.grayScale500 }}>{label}</p>
                  <p className="font-medium" style={{ color: colors.textColor }}>{value || '-'}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Edit form */}
          {editMode && (
            <div className="rounded-xl p-5" style={{ backgroundColor: colors.cardBackground || colors.backgroundColor, boxShadow: colors.shadowSoft }}>
              <h3 className="font-semibold mb-4" style={{ color: colors.textColor }}>Edit User</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {['name', 'email', 'phone_number'].map((field) => (
                  <input
                    key={field}
                    placeholder={field.replace('_', ' ')}
                    defaultValue={field === 'name' ? user.profile?.name : user[field]}
                    onChange={(e) => setEditForm((f) => ({ ...f, [field]: e.target.value }))}
                    className="px-3 py-2 rounded-lg text-sm outline-none"
                    style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}`, color: colors.textColor }}
                  />
                ))}
              </div>
              <button onClick={handleSaveEdit} className="mt-3 px-4 py-2 rounded-lg text-sm font-medium text-white" style={{ backgroundColor: colors.primary }}>
                Save Changes
              </button>
            </div>
          )}

          {/* Subscription */}
          <div className="rounded-xl p-5" style={{ backgroundColor: colors.cardBackground || colors.backgroundColor, boxShadow: colors.shadowSoft }}>
            <h3 className="font-semibold mb-4" style={{ color: colors.textColor }}>Subscription</h3>
            <div className="flex flex-wrap gap-3 items-end">
              <select
                value={subTier}
                onChange={(e) => setSubTier(e.target.value)}
                className="px-3 py-2 rounded-lg text-sm outline-none"
                style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}`, color: colors.textColor }}
              >
                <option value="free">Free</option>
                <option value="pro">Pro</option>
                <option value="premium">Premium</option>
              </select>
              <input
                type="date"
                value={subExpires?.split('T')[0] || ''}
                onChange={(e) => setSubExpires(e.target.value)}
                className="px-3 py-2 rounded-lg text-sm outline-none"
                style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}`, color: colors.textColor }}
              />
              <button onClick={handleSubUpdate} className="px-4 py-2 rounded-lg text-sm font-medium text-white" style={{ backgroundColor: colors.primary }}>
                Update Subscription
              </button>
            </div>
          </div>

          {/* Wallet adjust */}
          <div className="rounded-xl p-5" style={{ backgroundColor: colors.cardBackground || colors.backgroundColor, boxShadow: colors.shadowSoft }}>
            <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: colors.textColor }}>
              {React.createElement(FiDollarSign as any, { size: 16 })} Wallet Adjustment
            </h3>
            <div className="flex flex-wrap gap-3 items-end">
              <select
                value={walletType}
                onChange={(e) => setWalletType(e.target.value)}
                className="px-3 py-2 rounded-lg text-sm outline-none"
                style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}`, color: colors.textColor }}
              >
                <option value="DEPOSIT">Credit</option>
                <option value="WITHDRAW">Debit</option>
              </select>
              <input
                type="number"
                placeholder="Amount"
                value={walletAmount}
                onChange={(e) => setWalletAmount(e.target.value)}
                className="px-3 py-2 rounded-lg text-sm outline-none w-28"
                style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}`, color: colors.textColor }}
              />
              <input
                placeholder="Description"
                value={walletDesc}
                onChange={(e) => setWalletDesc(e.target.value)}
                className="px-3 py-2 rounded-lg text-sm outline-none flex-1 min-w-[150px]"
                style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}`, color: colors.textColor }}
              />
              <button onClick={handleWalletAdjust} className="px-4 py-2 rounded-lg text-sm font-medium text-white" style={{ backgroundColor: colors.primary }}>
                Adjust
              </button>
            </div>
          </div>

          {/* Reset queries */}
          <div className="rounded-xl p-5" style={{ backgroundColor: colors.cardBackground || colors.backgroundColor, boxShadow: colors.shadowSoft }}>
            <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: colors.textColor }}>
              {React.createElement(FiRefreshCw as any, { size: 16 })} Copilot Queries
            </h3>
            <p className="text-sm mb-3" style={{ color: colors.grayScale500 }}>
              Current usage: {user.profile?.copilot_queries_used || 0} queries
            </p>
            <button onClick={handleResetQueries} className="px-4 py-2 rounded-lg text-sm font-medium text-white" style={{ backgroundColor: '#F59E0B' }}>
              Reset to 0
            </button>
          </div>
        </div>
      )}

      {activeTab === 'bookings' && (
        <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${colors.borderColor}` }}>
          {user.bookings?.length === 0 ? (
            <p className="p-6 text-center text-sm" style={{ color: colors.grayScale500 }}>No bookings</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr style={{ backgroundColor: colors.inputBackground }}>
                  <th className="text-left px-4 py-3 text-xs uppercase" style={{ color: colors.grayScale500 }}>ID</th>
                  <th className="text-left px-4 py-3 text-xs uppercase" style={{ color: colors.grayScale500 }}>Time</th>
                  <th className="text-left px-4 py-3 text-xs uppercase" style={{ color: colors.grayScale500 }}>Type</th>
                  <th className="text-left px-4 py-3 text-xs uppercase" style={{ color: colors.grayScale500 }}>Booked</th>
                </tr>
              </thead>
              <tbody>
                {user.bookings?.map((b: any) => (
                  <tr key={b.id} style={{ borderTop: `1px solid ${colors.borderColor}` }}>
                    <td className="px-4 py-3" style={{ color: colors.textColor }}>{b.id}</td>
                    <td className="px-4 py-3" style={{ color: colors.textColor }}>{b.start_time?.split('T')[0]}</td>
                    <td className="px-4 py-3" style={{ color: colors.textColor }}>{b.booking_type}</td>
                    <td className="px-4 py-3" style={{ color: colors.textColor }}>{b.is_booked ? 'Yes' : 'No'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'transactions' && (
        <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${colors.borderColor}` }}>
          {user.transactions?.length === 0 ? (
            <p className="p-6 text-center text-sm" style={{ color: colors.grayScale500 }}>No transactions</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr style={{ backgroundColor: colors.inputBackground }}>
                  <th className="text-left px-4 py-3 text-xs uppercase" style={{ color: colors.grayScale500 }}>ID</th>
                  <th className="text-left px-4 py-3 text-xs uppercase" style={{ color: colors.grayScale500 }}>Type</th>
                  <th className="text-left px-4 py-3 text-xs uppercase" style={{ color: colors.grayScale500 }}>Amount</th>
                  <th className="text-left px-4 py-3 text-xs uppercase" style={{ color: colors.grayScale500 }}>Status</th>
                  <th className="text-left px-4 py-3 text-xs uppercase" style={{ color: colors.grayScale500 }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {user.transactions?.map((t: any) => (
                  <tr key={t.id} style={{ borderTop: `1px solid ${colors.borderColor}` }}>
                    <td className="px-4 py-3" style={{ color: colors.textColor }}>{t.id}</td>
                    <td className="px-4 py-3" style={{ color: colors.textColor }}>{t.type}</td>
                    <td className="px-4 py-3" style={{ color: colors.textColor }}>Rs {t.amount}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{
                        backgroundColor: t.status === 'SUCCESS' ? '#10B98120' : t.status === 'REFUNDED' ? '#F59E0B20' : '#EF444420',
                        color: t.status === 'SUCCESS' ? '#10B981' : t.status === 'REFUNDED' ? '#F59E0B' : '#EF4444',
                      }}>{t.status}</span>
                    </td>
                    <td className="px-4 py-3" style={{ color: colors.grayScale500 }}>{t.created_at?.split('T')[0]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'copilot' && (
        <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${colors.borderColor}` }}>
          {user.conversations?.length === 0 ? (
            <p className="p-6 text-center text-sm" style={{ color: colors.grayScale500 }}>No conversations</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr style={{ backgroundColor: colors.inputBackground }}>
                  <th className="text-left px-4 py-3 text-xs uppercase" style={{ color: colors.grayScale500 }}>ID</th>
                  <th className="text-left px-4 py-3 text-xs uppercase" style={{ color: colors.grayScale500 }}>Title</th>
                  <th className="text-left px-4 py-3 text-xs uppercase" style={{ color: colors.grayScale500 }}>Created</th>
                </tr>
              </thead>
              <tbody>
                {user.conversations?.map((c: any) => (
                  <tr key={c.id} style={{ borderTop: `1px solid ${colors.borderColor}` }}>
                    <td className="px-4 py-3" style={{ color: colors.textColor }}>{c.id}</td>
                    <td className="px-4 py-3" style={{ color: colors.textColor }}>{c.title || 'Untitled'}</td>
                    <td className="px-4 py-3" style={{ color: colors.grayScale500 }}>{c.created_at?.split('T')[0]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Modals */}
      {modal.open && modal.type === 'blacklist' && (
        <AdminConfirmModal
          title={user.is_blacklisted ? 'Unblock User' : 'Blacklist User'}
          message={user.is_blacklisted
            ? 'This will restore the user\'s access to the platform.'
            : 'This will suspend the user\'s account. They will not be able to log in.'}
          confirmLabel={user.is_blacklisted ? 'Unblock' : 'Blacklist'}
          destructive={!user.is_blacklisted}
          onConfirm={handleBlacklist}
          onCancel={() => setModal({ type: '', open: false })}
        />
      )}
      {modal.open && modal.type === 'delete' && (
        <AdminConfirmModal
          title="Delete User"
          message="This will permanently delete the user and all their data. This action cannot be undone."
          confirmLabel="Delete"
          destructive
          onConfirm={handleDelete}
          onCancel={() => setModal({ type: '', open: false })}
        />
      )}
    </div>
  );
};

export default AdminUserDetail;
